import { Geometry, Point } from 'geojson'
import { Analyzer } from 'type-analyzer'
import { QueryParameters } from '@carto/api-client'
import { parseSync } from '@loaders.gl/core'
import { WKTLoader } from '@loaders.gl/wkt'
import { TilejsonResult } from '@carto/api-client'
import { ProtoDataset } from '@carto/kepler.gl/actions'
import { parseRowsByFields, ProcessorResult, processRowObject } from '@carto/kepler.gl/processors'

import { hexToRGB } from 'features/common/utils/colors'
import { ProviderType } from 'features/connections'
import { SchemaField, SchemaFieldType } from 'features/data-explorer/types'
import { Formats, MapType } from 'features/maps/types'

import { attributeHasStats, isDynamicSpatialIndexDataset } from '../builderSliceUtils'
import { MULTISELECT_FILTER_TYPE, RANGE_FILTER_TYPE } from 'features/builder/ui/builderConstants'
import { AttributeStats, AttributeType } from 'features/builder/types'
import { mapSchemaTypeToAttributeType } from 'features/builder/utils/tilestats'

interface buildKeplerDatasetProps {
  id: string
  label: string
  color: string
  connection?: string
  format?: Formats
  data: Record<string, unknown>[]
  type: MapType
  source: string
  schema?: SchemaField[]
  provider: ProviderType
  geoColumn?: string | null
  columns?: string[] | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  accessToken?: string
  queryParameters?: QueryParameters
}

export function buildKeplerDataset({
  id,
  label,
  color,
  connection,
  format,
  data,
  type,
  source,
  schema,
  provider,
  geoColumn,
  columns,
  aggregationExp,
  aggregationResLevel,
  accessToken,
  queryParameters
}: buildKeplerDatasetProps): ProtoDataset | undefined {
  // We read this variable in carto-kepler
  const isDynamicSpatialIndex = isDynamicSpatialIndexDataset({ type, format, geoColumn })

  if (format === Formats.RASTER || format === Formats.TILEJSON) {
    return builderKeplerDatasetFromTileJSON({
      id,
      label,
      color,
      connection,
      tilejson: data as unknown as TilejsonResult,
      schema,
      type,
      source,
      geoColumn,
      columns,
      provider,
      isDynamicSpatialIndex,
      aggregationExp,
      aggregationResLevel,
      accessToken,
      queryParameters
    })
  }

  if (format === Formats.JSON) {
    return buildKeplerDatasetFromJSON({
      id,
      label,
      color,
      connection,
      data,
      type,
      source,
      schema,
      provider,
      geoColumn,
      isDynamicSpatialIndex
    })
  }

  return undefined
}

function builderKeplerDatasetFromTileJSON({
  id,
  label,
  color,
  connection,
  tilejson,
  schema,
  type,
  source,
  geoColumn,
  columns,
  provider,
  isDynamicSpatialIndex,
  aggregationExp,
  aggregationResLevel,
  accessToken,
  queryParameters
}: {
  id: string
  label: string
  color: string
  connection?: string
  tilejson: TilejsonResult | null | undefined
  schema?: SchemaField[]
  type: MapType
  source: string
  geoColumn?: string | null
  columns?: string[] | null
  provider: ProviderType
  isDynamicSpatialIndex: boolean
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  accessToken?: string
  queryParameters?: QueryParameters
}): ProtoDataset {
  if (!tilejson && schema) {
    return {
      info: { id, label, color: hexToRGB(color) },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      data: parseSchemaToKeplerData(schema) as any,
      metadata: {
        tilejson,
        type: MapType.tileset,
        source,
        sourceType: type,
        schema,
        connection,
        geoColumn,
        columns,
        provider,
        isDynamicSpatialIndex,
        queryParameters,
        tilestats: {
          attributes: schema
            .map(({ type, name }) => ({
              attribute: name,
              type: mapSchemaTypeToAttributeType(type) as AttributeType
            }))
            .filter((attribute) => attribute.type)
        },
        vector_layers: [
          {
            id: 'data_layer',
            fields: schema.reduce((acc, { name, type }) => {
              const attributeType = mapSchemaTypeToAttributeType(type)
              if (attributeType) {
                acc[name] = attributeType
              }
              return acc
            }, {} as Record<string, AttributeType>)
          }
        ]
      }
    }
  }
  if (!tilejson) throw new Error('Tilejson is required to build a kepler dataset')

  // Builder is nasty and modifies tilejson internally, so ensure that we only do it
  // on local copy
  tilejson = structuredClone(tilejson)
  processTilestats(tilejson)

  const tilestats = tilejson.tilestats?.layers[0] || getDefaultTilestats(tilejson)
  const bounds = tilejson.bounds
  const center = tilejson.center
  const vector_layers = tilejson.vector_layers
  const fractionDroppedPerZoom = tilejson.fraction_dropped_per_zoom || []
  const raster_metadata = tilejson.raster_metadata

  return {
    info: { id, label, color: hexToRGB(color) },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: parseTilesetToKeplerData(tilejson) as any,
    metadata: {
      tilejson,
      tilestats,
      bounds,
      center,
      vector_layers,
      raster_metadata,
      type: type === MapType.raster ? MapType.raster : MapType.tileset,
      source,
      sourceType: type,
      dataresolution: tilejson.dataresolution,
      aggregationExp,
      aggregationResLevel,
      scheme: tilejson.scheme,
      schema,
      connection,
      geoColumn,
      columns,
      provider,
      isDynamicSpatialIndex,
      fractionDroppedPerZoom,
      accessToken,
      queryParameters
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getDefaultTilestats(tileset: TilejsonResult) {
  return {
    attributes: [],
    geometry: tileset.vector_layers?.[0]?.geometry_type
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function processTilestats(tilejson: TilejsonResult) {
  const attributes = tilejson.tilestats?.layers[0]?.attributes

  if (!attributes) {
    return
  }

  // Static spatial index tilesets return the stats by resolution so we get the
  // global resolution and ignore the rest. We modify the original tilejson to keep the compatibility
  tilejson.tilestats.layers[0].attributes = attributes.map((attribute) => {
    return Object.entries(attribute).reduce((prev, [key, value]) => {
      if (Object.prototype.hasOwnProperty.call(value, 'global')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        prev[key] = (value as any).global
        return prev
      }
      prev[key] = value
      return prev
    }, {} as typeof attribute)
  })
}

export function parseSchemaToKeplerData(schema: SchemaField[]) {
  const parsedAttributes = schema.reduce((acc, field) => {
    const { name, type } = field
    switch (type) {
      case 'string':
        // this fake value for kepler to detect it as actual string
        acc[name] = 'sampleString'
        break
      case 'number':
        acc[name] = 0
        break
      case 'boolean':
        acc[name] = false
        break
      case 'timestamp':
        acc[name] = new Date().toISOString()
        break
    }
    return acc
  }, {} as Record<string, string | number | boolean | Date>)

  const data = processRowObject([{ ...parsedAttributes }])

  return data
}

function getSampleRowVectorTileset(tilejson: TilejsonResult) {
  const attributes = (tilejson.vector_layers && tilejson.vector_layers[0].fields) || {}
  return Object.keys(attributes).reduce((acc, current) => {
    const type = attributes[current].toLocaleLowerCase()

    switch (type) {
      case 'string':
        // this fake value for kepler to detect it as actual string
        acc[current] = 'sampleString'
        break
      case 'number':
        acc[current] = 0
        break
      case 'boolean':
        acc[current] = false
        break
      case 'timestamp':
        acc[current] = new Date().toISOString()
        break
    }
    return acc
  }, {} as Record<string, string | number | boolean | Date>)
}

function getSampleRowRaster(tilejson: TilejsonResult) {
  return tilejson?.raster_metadata?.bands.reduce((acc, band) => {
    acc[band.name] = band.stats.min || 0
    return acc
  }, {} as Record<string, number>)
}

function parseTilesetToKeplerData(tilejson: TilejsonResult) {
  const stats = tilejson.tilestats?.layers[0].attributes
  const parsedAttributes = tilejson.raster_metadata ? getSampleRowRaster(tilejson) : getSampleRowVectorTileset(tilejson)

  const data = processRowObject([{ ...parsedAttributes }])

  data?.fields.map((f) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const stat = stats?.find((s: any) => s.attribute === f.name) as AttributeStats | undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let domain: any[] = []
    let value: Array<string | number> = []
    let type
    let gpu
    if (stat && attributeHasStats(stat)) {
      if (stat.type === AttributeType.String || stat.type === AttributeType.Boolean) {
        gpu = false
        ;(domain = stat.categories.map((c: { category: string }) => c.category)), (type = MULTISELECT_FILTER_TYPE)
      } else if (stat.type === AttributeType.Number || stat.type === AttributeType.Timestamp) {
        gpu = true
        domain = [stat.min, stat.max]
        value = [...domain]
        type = RANGE_FILTER_TYPE
      }
    }
    f.filterProps = {
      fieldType: f.type,
      domain,
      value,
      histogram: null,
      gpu,
      step: 1,
      type
    }
    return f
  })

  return data
}

interface buildKeplerDatasetFromJSONProps extends Omit<buildKeplerDatasetProps, 'format'> {
  data: Record<string, unknown>[]
  isDynamicSpatialIndex: boolean
}

function buildKeplerDatasetFromJSON({
  id,
  label,
  color,
  data,
  type,
  schema,
  provider,
  geoColumn,
  isDynamicSpatialIndex
}: buildKeplerDatasetFromJSONProps): ProtoDataset {
  let keplerData
  try {
    const [preparedRows, newSchema] = parseDataToKeplerData(data, schema, geoColumn)
    if (preparedRows) {
      if (newSchema) {
        keplerData = processObjectsWithSchema(preparedRows, newSchema)
      } else {
        keplerData = processRowObject(preparedRows)
      }
    }
  } catch (error) {
    throw new Error(`Error parsing data to kepler format ${(error as Error).message}`)
  }

  return {
    info: { id, label, color: hexToRGB(color) },
    data: keplerData,
    metadata: {
      type,
      schema,
      provider,
      isDynamicSpatialIndex,
      geoColumn
    }
  }
}

export function parseDataToKeplerData(
  data: Record<string, unknown>[],
  schema?: SchemaField[],
  geoColumn?: string | null
): [Record<string, unknown>[], SchemaField[] | undefined] {
  if (data.length > 0) {
    const columnInfo = findGeomColumn(data, geoColumn)
    if (columnInfo && columnInfo.type === 'point') {
      const { column: pointColumn, format: pointColumnFormat } = columnInfo

      if (schema) {
        // update schema with artifically added latitude, longitude
        schema = [
          ...schema.filter((sf) => sf.name !== pointColumn),
          { name: 'latitude', type: SchemaFieldType.Number, artificial: true },
          { name: 'longitude', type: SchemaFieldType.Number, artificial: true }
        ]
      }

      if (pointColumnFormat === 'geojson') {
        data = parsePointDatasetGeoJson(data, pointColumn)
      } else if (pointColumnFormat === 'wkt') {
        data = parsePointDatasetWkt(data, pointColumn)
      }
    }
  }
  return [data, schema]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isGeoJsonPoint(value: any): value is Point {
  return (
    value !== null &&
    typeof value === 'object' &&
    value.type === 'Point' &&
    Array.isArray(value.coordinates) &&
    value.coordinates.length > 0
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isGeoJsonGeometry(value: any): value is Geometry {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof value.type === 'string' &&
    Array.isArray(value.coordinates) &&
    value.coordinates.length > 0
  )
}

function parsePointDatasetWkt(data: Record<string, unknown>[], pointColumn: string) {
  const features: Record<string, unknown>[] = []
  for (let i = 0; i < data.length; i++) {
    const row = data[i]
    try {
      const geom = parseSync(row[pointColumn], WKTLoader)
      if (isGeoJsonPoint(geom)) {
        delete row[pointColumn]
        row.longitude = geom.coordinates[0]
        row.latitude = geom.coordinates[1]
        features.push(row)
      }
    } catch (error) {
      // skip the row
      continue
    }
  }

  return features
}

function parsePointDatasetGeoJson(data: Record<string, unknown>[], pointColumn: string) {
  const features: Record<string, unknown>[] = []
  for (let i = 0; i < data.length; i++) {
    const row = data[i]
    const geom = row[pointColumn]
    if (isGeoJsonPoint(geom)) {
      delete row[pointColumn]
      row.longitude = geom.coordinates[0]
      row.latitude = geom.coordinates[1]
      features.push(row)
    }
  }
  return features
}

type GeomColumnInfo = {
  column: string
  type: 'point' | 'other'
  format: 'geojson' | 'wkt'
}

function findGeomColumn(sampleRows: Record<string, unknown>[], geoColumn?: string | null): GeomColumnInfo | null {
  for (const row of sampleRows) {
    if (geoColumn) {
      const value = row[geoColumn]
      if (!value) {
        continue
      }
      return detectGeomValueType(value, geoColumn)
    } else {
      for (const column in row) {
        const info = detectGeomValueType(row[column], column)
        if (info) {
          return info
        }
      }
    }
  }
  return null
}

function detectGeomValueType(value: unknown, key: string): GeomColumnInfo | null {
  if (isGeoJsonGeometry(value)) {
    if (isGeoJsonPoint(value)) {
      return { column: key, type: 'point', format: 'geojson' }
    } else {
      return { column: key, type: 'other', format: 'geojson' }
    }
  } else if (typeof value === 'string') {
    try {
      const geometry = parseSync(value, WKTLoader)
      if (geometry) {
        if (geometry.type === 'Point') {
          return { column: key, type: 'point', format: 'wkt' }
        } else {
          return { column: key, type: 'other', format: 'wkt' }
        }
      }
    } catch (error) {
      // skip this column
    }
  }
  return null
}

/**
 * Process raw data with schema to tabular format supported by kepler.
 *
 * Uses schema provided by backend instead of kepler autoguessing, must
 * maintain same result type as `processRowObject` from `@carto/kepler.gl/processors`
 */
export function processObjectsWithSchema(objects: Record<string, unknown>[], schema: SchemaField[]): ProcessorResult {
  const keys = Object.keys(objects[0])
  const rows = objects.map((d) => keys.map((key) => d[key]))
  const fields = keys.map((key, index) => {
    const schemaField = schema?.find((sf) => sf.name === key)
    const samples = getAnalyzerSample(rows, 10, index)
    const { type, analyzerType, format } = cartoTypeToKeplerTypeInfo(schemaField?.type, samples)
    return {
      name: key,
      id: key,
      displayName: key,
      fieldIdx: index,
      type,
      artificial: schemaField?.artificial || false,
      format: format || '',
      analyzerType,
      valueAccessor: (dc) => (d) => {
        return dc.valueAt(d.index, index)
      }
    }
  })
  return {
    rows: parseRowsByFields(rows, fields),
    fields
  }
}

function getAnalyzerSample(rows: unknown[][], count: number, colIdx: number) {
  const result: unknown[] = []
  for (let i = 0; i < rows.length; ++i) {
    const v = rows[i][colIdx]
    if (v === null || v === undefined) {
      continue
    }
    result.push({ x: v })
    if (result.length >= count) {
      return result
    }
  }
  return result
}

/**
 * Convert carto schema type to type-analyzer type.
 *
 * Fallback to 'STRING' for source types with no obvious mapping.
 *
 * @see https://www.npmjs.com/package/type-analyzer
 */
function cartoTypeToKeplerTypeInfo(
  type: string | undefined,
  samples: unknown[]
): { type: string; analyzerType: string; format?: string } {
  switch (type) {
    case 'boolean':
      return {
        type: 'boolean',
        analyzerType: 'BOOLEAN'
      }
    case 'string':
      return {
        type: 'string',
        analyzerType: 'STRING'
      }
    case 'number': {
      const meta = Analyzer.computeColMeta(samples)[0]
      return {
        type: meta?.type === 'INT' ? 'integer' : 'real',
        analyzerType: meta?.type
      }
    }
    case 'geometry':
      return {
        type: 'geojson',
        analyzerType: 'GEOMETRY'
      }

    case 'timestamp': {
      // For timestamp, kepler is very picky and ensures that analyzerType, format and type
      // are very well defined. If the type/analyzerType/format don't match it will ignore
      // our schema.
      const meta = Analyzer.computeColMeta(samples)[0]
      return {
        type: meta?.type === 'DATE' ? 'date' : 'timestamp',
        analyzerType: meta?.type,
        format: meta?.format
      }
    }
    case 'object':
      return {
        type: 'geojson',
        analyzerType: 'OBJECT'
      }
    case 'variant':
    case 'unknown':
    default:
      return {
        type: 'string',
        analyzerType: 'STRING'
      }
  }
}
